/**
 * As frases que uma pessoa lê quando não consegue entrar.
 *
 * ─── Porque é que isto é um módulo e não três `if` em três ecrãs ───────────
 *
 * Os ecrãs de entrada (`/login` e `/pin`) traduzem respostas HTTP em frases, e
 * cada um deles tinha a sua tradução escrita à mão. O resultado foi o mesmo
 * defeito a aparecer duas vezes, com dois anos de distância e por duas razões
 * diferentes:
 *
 *   • 2026-10-20 — a base saturou, as consultas devolveram `PGRST002`, as
 *     rotas responderam 503, e o ecrã do PIN disse «PIN incorreto». Toda a
 *     gente tinha o PIN certo; a frase mandou-as mudá-lo.
 *   • 2026-10-27 — o PostgREST ficou sem cache de esquema (expunha um schema
 *     já largado), as rotas responderam 503 outra vez, e o `/login` disse
 *     «Erro de ligação. Tente novamente.». A ligação estava boa; a frase
 *     mandou toda a gente olhar para a internet do próprio PC.
 *
 * Nos dois casos a avaria era a mesma, a resposta do servidor dizia-o, e o
 * ecrã escreveu outra coisa. A tradução passa a viver num sítio só, onde se lê
 * de uma vez o que cada código de resposta significa para quem está à espera
 * de entrar.
 *
 * ─── A regra ──────────────────────────────────────────────────────────────
 *
 * **Culpar as credenciais só com 401; culpar a rede só quando não houve
 * resposta.** Tudo o que esteja pelo meio é o serviço, e diz-se que é o
 * serviço — porque é a única frase que não manda a pessoa mudar uma credencial
 * que está certa nem chamar quem lhe gere a internet.
 *
 * ─── E porque não se mostra a mensagem do servidor ────────────────────────
 *
 * Porque só uma delas foi escrita para ser lida por pessoas (a do
 * `servicoIndisponivel()`). As outras são para quem programa — «Invalid
 * request body», «Invalid credentials», «Too many requests. Please try again
 * later.» —, estão em inglês e aparecem a quem só quer entrar. Um `||
 * resposta.error` parece defensivo e é como o inglês chega ao ecrã: as frases
 * daqui são as únicas que a pessoa vê, e as do servidor ficam para os logs.
 */

/** Não houve resposta: o `fetch` rebentou. É o único caso que é mesmo a rede. */
export const SEM_RESPOSTA_DO_SERVIDOR =
  "Não foi possível falar com o servidor. Verifique a ligação à internet e tente novamente.";

/**
 * Houve resposta, e ela não fala das credenciais. Repete a promessa que o
 * `servicoIndisponivel()` já faz do lado do servidor, e por isso não há
 * tradução a divergir: a pessoa precisa de saber que não é culpa dela.
 */
export const SERVICO_INDISPONIVEL =
  "Serviço temporariamente indisponível. Não é um problema das suas credenciais — tente novamente dentro de um minuto.";

/** O 429 do `rateLimitedResponse`, que manda esperar 60 segundos. */
export const DEMASIADAS_TENTATIVAS =
  "Demasiadas tentativas. Aguarde um minuto e tente novamente.";

/**
 * As duas frases que mudam de ecrã para ecrã, porque dependem do que se estava
 * a fazer:
 *
 * - `credenciais` — o 401. É a única resposta que autoriza dizer que o que a
 *   pessoa escreveu está errado.
 * - `pedidoInvalido` — o 400. Não é uma avaria do serviço nem uma credencial
 *   errada: é o pedido a não passar a validação do `zod`, e o que a pessoa
 *   pode fazer a seguir depende do ecrã. No `/login` é quase sempre o e-mail
 *   (o `type="email"` do browser aceita `ana@zunox`, que o `zod` recusa); no
 *   `/pin` é a identidade guardada estar estragada, e aí a saída é voltar ao
 *   início em vez de insistir no teclado.
 */
export interface FrasesDoEcra {
  credenciais: string;
  pedidoInvalido: string;
}

/**
 * Traduz o status de uma resposta que **chegou** na frase que a pessoa lê.
 *
 * Chamar isto só com `!res.ok`. O `catch` do `fetch` é o outro caminho e tem a
 * sua constante — distingui-los é o ponto todo deste módulo.
 */
export function mensagemDeEntrada(status: number, frases: FrasesDoEcra): string {
  if (status === 429) return DEMASIADAS_TENTATIVAS;
  if (status === 401) return frases.credenciais;
  if (status === 400) return frases.pedidoInvalido;

  // Qualquer outro código: o servidor respondeu, portanto a rede está de pé, e
  // não foi sobre as credenciais. 503 do `servicoIndisponivel()`, 500 de uma
  // excepção não apanhada, 502/504 da Vercel, 404 de um deploy a meio — para
  // quem está à porta é tudo a mesma coisa, e é tudo «não é de ti, espera um
  // minuto». Não se enumera aqui o que não se consegue distinguir.
  return SERVICO_INDISPONIVEL;
}
